import React, { useState } from 'react';
import { cn } from '@/utils/cn';
import { GRID, LIST } from '@/constants';
import { useStickySectionScroll } from '@/app/hooks/useStickySectionScroll';
import { usePathname } from 'next/navigation';
import { setSortingFilters } from '@/redux/reducers/products-reducer';
import { useDispatch, useSelector } from 'react-redux';
import CategorySort from '@/app/components/Sort/CategorySort';
import { setSortingFilters as setSearchSortingFilters } from '@/redux/reducers/search-reducer';
import SearchProductListingFilter from '@/app/search-result/components/SearchProductListingFilter';
import { cardViewTypes } from '@/constants/types';
import { toggleView } from '@/redux/reducers/product-view-option-reducer';
import ProductListingFilter from './ProductListingFilter';
import TextLarge from '../Typography/TextLarge';
import ViewToggler from '../Input/ViewToggler';
import TextBase from '../Typography/TextBase';
import FilterButton from './components/FilterButton';

function FilterSortControlBar({
  showForSearchResult = false,
  totalProductsCount,
  openMenu,
  isCategory,
}) {
  const dispatch = useDispatch();
  const pathname = usePathname();

  // hooks
  // scroll on for plp page
  const isSectionSticky = useStickySectionScroll();

  const { view } = useSelector((state) => state.productViewOption);

  // states
  const [toggleIcon, setToggleIcon] = useState(
    view === cardViewTypes.list ? GRID : LIST,
  );
  const [isOpenProductListingFilter, setIsOpenProductListingFilter] =
    useState(false);

  const handleViewToggle = () => {
    const newIcon = toggleIcon === LIST ? GRID : LIST;
    setToggleIcon(newIcon);

    const newView = newIcon === LIST ? cardViewTypes.grid : cardViewTypes.list;
    dispatch(toggleView(newView));
  };

  const handleFilterToggle = () => {
    setIsOpenProductListingFilter((prevValue) => !prevValue);
  };

  const handleCloseProductListingFilter = (data) =>
    setIsOpenProductListingFilter(data);

  return (
    <>
      <section
        className={cn('', {
          'filter-sort-control-bar shadow-filter-card pb-[12px]':
            isSectionSticky && !showForSearchResult,

          'sticky top-[56px] lg:top-[100px] z-[198] transition-all duration-500 ease-in-out bg-white':
            isSectionSticky && showForSearchResult,
        })}
      >
        <div
          className={cn(
            'filter-and-sorting container mx-auto bg-white flex items-center justify-between gap-[10px]',
            {
              'mb-[16px] lg:mb-[24px]': !isSectionSticky,
              'pt-[56px] lg:pt-[92px]': openMenu,
              'py-10': isSectionSticky && showForSearchResult,
            },
          )}
        >
          {showForSearchResult ? (
            <>
              {totalProductsCount ? (
                <TextBase
                  text={
                    totalProductsCount
                      ? `${totalProductsCount} items found`
                      : '0 item found'
                  }
                  className="text-senate font-medium lg:font-semibold"
                />
              ) : null}

              <div className="flex items-center gap-[10px]">
                <FilterButton onFilter={handleFilterToggle} />
                <CategorySort
                  onSort={(data) => dispatch(setSearchSortingFilters(data))}
                />
              </div>
            </>
          ) : (
            <>
              <div className="block lg:hidden">
                <CategorySort
                  onSort={(data) => dispatch(setSortingFilters(data))}
                />
              </div>

              <TextBase
                text={
                  totalProductsCount
                    ? `${totalProductsCount} items found`
                    : '0 item found'
                }
                className="hidden lg:block text-senate font-medium lg:font-semibold"
              />

              <div className="flex items-center gap-[10px]">
                <FilterButton onFilter={handleFilterToggle} />

                <div className="hidden lg:block">
                  <CategorySort
                    onSort={(data) => dispatch(setSortingFilters(data))}
                  />
                </div>

                {/* grid-list toggle button */}
                {pathname.startsWith('/category') ||
                pathname === '/products' ||
                pathname.startsWith('/brand') ? (
                  <ViewToggler icon={toggleIcon} onClick={handleViewToggle} />
                ) : null}
              </div>
            </>
          )}
        </div>

        {!showForSearchResult ? (
          <section
            className={cn('container', {
              hidden: isSectionSticky,
            })}
          >
            <TextLarge
              text={
                totalProductsCount
                  ? `${totalProductsCount} items found`
                  : '0 item found'
              }
              className={cn(
                'block lg:hidden text-xs lg:text-lg text-senate font-medium lg:font-semibold mb-12',
                {
                  'mb-0': isSectionSticky,
                },
              )}
            />
          </section>
        ) : null}
      </section>

      {/* product listing filter */}
      {!showForSearchResult ? (
        <ProductListingFilter
          onView={isOpenProductListingFilter && !showForSearchResult}
          onClose={handleCloseProductListingFilter}
          isCategory={isCategory}
        />
      ) : null}

      {/* search product listing filter */}
      {showForSearchResult ? (
        <SearchProductListingFilter
          onView={isOpenProductListingFilter && showForSearchResult}
          onClose={handleCloseProductListingFilter}
        />
      ) : null}
    </>
  );
}

export default FilterSortControlBar;
